import { useEffect } from 'react';

import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import GroupIcon from '@material-ui/icons/GroupOutlined';
import { Skeleton } from '@material-ui/lab';

import { useAppDispatch, useAppSelector } from 'store';
import {
  EquipeInterfaceV2,
  equipesSelector,
  fetchEquipesPorAtendente,
} from 'store/reducers/equipes';

const useStyles = makeStyles(({ palette, typography, spacing }) => ({
  item: {
    borderRadius: 4,
    border: '1px solid transparent',
  },
  itemSelecionado: {
    backgroundColor: palette.action.selected,
    border: `1px solid ${palette.divider}`,
    '&:hover': {
      backgroundColor: palette.action.selected,
    },
  },
  checkIcon: {
    fontSize: 16,
    color: palette.primary.main,
  },
  sectionLabel: {
    fontSize: typography.pxToRem(12),
    fontWeight: 600,
    marginBottom: spacing(1),
    color: palette.text.secondary,
  },
}));

type ListaEquipesAtendenteProps = {
  userId: string;
  selectedId: string | undefined;
  onSelect: (equipe: EquipeInterfaceV2) => void;
};

export default function ListaEquipesAtendente({
  userId,
  selectedId,
  onSelect,
}: ListaEquipesAtendenteProps) {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { equipesPorAtendente, loadingEquipesPorAtendente } =
    useAppSelector(equipesSelector);

  useEffect(() => {
    if (userId) dispatch(fetchEquipesPorAtendente(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (
      !loadingEquipesPorAtendente &&
      equipesPorAtendente.length === 1 &&
      selectedId !== equipesPorAtendente[0].id
    ) {
      onSelect(equipesPorAtendente[0]);
    }
  }, [loadingEquipesPorAtendente, equipesPorAtendente, selectedId, onSelect]);

  const items = loadingEquipesPorAtendente
    ? Array.from(new Array(3) as EquipeInterfaceV2[])
    : equipesPorAtendente;

  const renderEquipes = items.map((equipe, index) => {
    const key = equipe ? equipe.id : index;
    const isSelected = equipe?.id === selectedId;

    if (equipe) {
      return (
        <ListItem
          key={key}
          button
          className={`${classes.item}${
            isSelected ? ` ${classes.itemSelecionado}` : ''
          }`}
          onClick={() => onSelect(equipe)}
        >
          <ListItemIcon>
            <GroupIcon />
          </ListItemIcon>
          <ListItemText
            primary={equipe.name}
            primaryTypographyProps={{ variant: 'body2' }}
          />
          {isSelected && (
            <ListItemSecondaryAction>
              <CheckIcon className={classes.checkIcon} />
            </ListItemSecondaryAction>
          )}
        </ListItem>
      );
    }

    return (
      <ListItem key={key}>
        <ListItemAvatar>
          <Skeleton variant="circle">
            <Avatar />
          </Skeleton>
        </ListItemAvatar>
        <ListItemText primary={<Skeleton variant="rect" />} />
      </ListItem>
    );
  });

  return (
    <List style={{ padding: 0, marginBottom: 16 }}>
      <Typography
        className={classes.sectionLabel}
        color="textSecondary"
        variant="body2"
      >
        Selecione a equipe do atendente:
      </Typography>

      {renderEquipes}

      {!loadingEquipesPorAtendente && equipesPorAtendente.length === 0 && (
        <ListItem disableGutters>
          <ListItemText secondary="Nenhuma equipe encontrada para este atendente." />
        </ListItem>
      )}
    </List>
  );
}
